import { apiClient } from "../../../lib/api-client";
import { API_PATHS } from "../../../config/api";
import { Variant } from "../../../types/app";

/**
 * Variant endpoints. Payloads are snake_case here (and camelCase on the upload
 * endpoints) — that mirrors the stored data on the backend and is deliberate.
 *
 * Listing is open to any user with project access; create / update / delete are
 * product-manager only and answer 403 for everyone else, so the UI hides those
 * controls rather than letting them fail.
 */
export const variantService = {
  getVariants: async (projectId: string | number): Promise<Variant[]> => {
    const data = await apiClient.get<Variant[]>(
      `${API_PATHS.GET_VARIANTS}?project_id=${encodeURIComponent(String(projectId))}`
    );
    return Array.isArray(data) ? data : [];
  },

  createVariant: async (variantData: {
    project_id: string | number;
    name: string;
    package_name: string;
    description?: string;
    /** Optional human-friendly alternate label. Sent as null when left blank. */
    alias?: string | null;
  }): Promise<{ id: string | number }> => {
    return apiClient.post(API_PATHS.VARIANT, variantData);
  },

  /**
   * Send only the fields being changed. `project_id` is ignored by the server —
   * moving a variant would strand the builds under it.
   */
  updateVariant: async (variantData: {
    id: string | number;
    name?: string;
    package_name?: string;
    description?: string;
    alias?: string | null;
  }): Promise<void> => {
    return apiClient.post(API_PATHS.UPDATE_VARIANT, variantData);
  },

  /**
   * A variant with builds under it fails the first attempt with a 400 ("This
   * variant still has apps…"). That is the prompt to confirm, then re-send with
   * `deleteApps` — which permanently deletes those builds and their files.
   */
  deleteVariant: async (
    variantId: string | number,
    deleteApps = false
  ): Promise<void> => {
    return apiClient.post(API_PATHS.DELETE_VARIANT, {
      id: variantId,
      ...(deleteApps ? { delete_apps: true } : {}),
    });
  },
};

export default variantService;
